// The user's font, size, color and call-to-action position choices from the Customization page in gtl.
// Only these keys are honored; anything else (including "") keeps the default
// look. gtl offers the same keys, so keep the two lists in step.

// Keys stored on the user record -> CSS font stacks (families from src/fonts.js).
// Satisfy and Coiny only come in weight 400, so text set in them uses that
// weight rather than letting the browser fake a bold.
const FONTS = {
  satisfy: { family: '"Satisfy", cursive', singleWeight: true },
  coiny: { family: '"Coiny", system-ui, sans-serif', singleWeight: true },
  "playfair-display": { family: '"Playfair Display Variable", serif' },
  roboto: { family: '"Roboto Variable", sans-serif' },
  outfit: { family: '"Outfit Variable", sans-serif' },
};

// The page's text roles, each with its own font and size on the user record. The
// role name is the suffix of Grid.css's --font-family-<role>, --font-weight-<role>
// and --font-scale-<role>. The label above the buttons uses the button role.
const ROLES = {
  handle: { font: "handleFont", size: "handleSize" },
  button: { font: "buttonFont", size: "buttonSize" },
  tab: { font: "tabFont", size: "tabSize" },
  cta: { font: "ctaFont", size: "ctaSize" },
};

// "" (or anything else) is Medium, the size Grid.css sets.
const SIZE_SCALES = { small: 0.9, large: 1.15 };

// Call-to-action positions other than the default (bottom). Middle centers it on the screen.
const CTA_POSITIONS = {
  middle: { "--cta-top": "50%", "--cta-bottom": "auto", "--cta-shift-y": "-50%" },
};

// --color-brand-coal, the default button text color.
const DARK_TEXT = [31, 31, 31];
// How far hover moves the button color toward its text color.
const HOVER_MIX = 0.08;

const rgb = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
const css = ([r, g, b]) => `rgb(${r}, ${g}, ${b})`;

/**
 * Button colors from the user's #rrggbb choices (already validated by userLoader). Text keeps
 * the default dark unless the user picked a color. Hover shifts the button color slightly
 * toward the text color.
 */
function buttonColors(bgHex, textHex) {
  const style = {};
  if (textHex) style["--button-text"] = textHex;
  if (bgHex) {
    const bg = rgb(bgHex);
    const text = textHex ? rgb(textHex) : DARK_TEXT;
    const hover = bg.map((c, i) => Math.round(c + (text[i] - c) * HOVER_MIX));
    style["--button-bg"] = bgHex;
    style["--button-bg-hover"] = css(hover);
  }
  return style;
}

// Own keys only, so a stored value like "constructor" can't match Object.prototype.
const lookup = (table, key) =>
  Object.prototype.hasOwnProperty.call(table, key) ? table[key] : undefined;

/**
 * Inline style for the element wrapping the grid page, or undefined when the
 * user hasn't customized their page (it then renders exactly as before).
 */
export function siteStyleFor(user) {
  const style = {};

  for (const [role, fields] of Object.entries(ROLES)) {
    const font = lookup(FONTS, user[fields.font]);
    if (font) {
      style[`--font-family-${role}`] = font.family;
      if (font.singleWeight) style[`--font-weight-${role}`] = 400;
    }
    const scale = lookup(SIZE_SCALES, user[fields.size]);
    if (scale) style[`--font-scale-${role}`] = scale;
  }

  Object.assign(style, buttonColors(user.buttonColor, user.buttonTextColor));
  if (user.handleColor) style["--text-color-handle"] = user.handleColor;
  if (user.ctaColor) style["--text-color-cta"] = user.ctaColor;
  if (user.tabColor) {
    // Inactive tabs are the same color at half strength, as the default grey is.
    style["--tab-color"] = user.tabColor;
    style["--tab-color-muted"] = `rgba(${rgb(user.tabColor).join(", ")}, 0.5)`;
  }

  const ctaPosition = lookup(CTA_POSITIONS, user.ctaPosition);
  if (ctaPosition) Object.assign(style, ctaPosition);

  return Object.keys(style).length ? style : undefined;
}
